import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { matchPath, NavLink, Outlet, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  UsersRound,
  FileText,
  BarChart3,
  MessageCircle,
  Settings as SettingsIcon,
  LogOut,
  Wrench,
  Images,
  Stamp,
  FileSpreadsheet,
  Moon,
  ShieldCheck,
  BookOpen,
  CalendarClock,
  ClipboardCheck,
  FilePenLine,
  ChevronDown,
  PanelLeftClose,
  PanelLeftOpen,
  Menu,
  X,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '../store/auth';
import { useChatUnread } from '../store/chat';
import { useSocket } from '../hooks/useSocket';
import { useBoardUpdates } from '../hooks/useBoardUpdates';
import { useTechniciansStore } from '../store/technicians';
import { requestNotificationPermission, showBrowserNotification } from '../utils/browserNotify';
import { playNotificationSound } from '../utils/sound';
import type { ChatMessage, GeneralChatMessage } from '../types';

const CHANNEL_LABELS: Record<string, string> = { general: 'общий чат', 'grease-trap': 'чат «Жирики»' };
import NotificationsBell from './NotificationsBell';
import UpdateBanner from './UpdateBanner';
import GlobalSearch from './GlobalSearch';
import { switchLayout } from '../utils/search';

// Название программы — Control Brigade, под ним компания (владелец, 08.10.2026: «название поменяй
// на Control Brigade, а ниже уже Труба-Дело»). В документах и SMS — по-прежнему «Труба-Дело»
const BASE_TITLE = 'Control Brigade';
const ICON_SIZE = 17;

interface NavItem {
  path: string;
  label: string;
  icon: LucideIcon;
  /** Подсвечивать только на точном адресе — иначе «/» горел бы на любой странице */
  end?: boolean;
  /** Счётчик справа от пункта */
  badge?: 'chatUnread';
  /** Другие слова, которыми ищут этот раздел: «зарплата» → «Отчёты», «1с» → «Выгрузка в 1С» */
  keywords?: string;
}

interface NavSection {
  /** Под этим ключом запоминается, свёрнута ли группа */
  id: string;
  /** Разделы без заголовка не сворачиваются */
  title?: string;
  /** Прижать к низу меню */
  bottom?: boolean;
  items: NavItem[];
}

// Вкладок становится всё больше, и плоский список из полутора десятков пунктов читался плохо —
// теперь они разложены по группам. Новый раздел — одна строка здесь, а не ещё один NavLink
const NAV_SECTIONS: NavSection[] = [
  {
    id: 'main',
    items: [
      { path: '/', label: 'Диспетчерская', icon: LayoutDashboard, end: true, keywords: 'наряды заявки главная карта доска канбан календарь мастера на карте' },
      { path: '/chat', label: 'Чат', icon: MessageCircle, badge: 'chatUnread', keywords: 'сообщения переписка написать мастеру' },
    ],
  },
  {
    id: 'orders',
    title: 'Заявки',
    items: [
      { path: '/night-orders', label: 'Ночные заявки', icon: Moon, keywords: 'ночь ночная смена сам завёл' },
      { path: '/maintenance', label: 'Обслуживание', icon: CalendarClock, keywords: 'ежемесячные сделки абонентское объекты' },
      { path: '/inspections', label: 'ПрофОсмотры', icon: ClipboardCheck, keywords: 'осмотры профосмотр проверка домов' },
    ],
  },
  {
    id: 'documents',
    title: 'Документы',
    items: [
      { path: '/acts', label: 'Акты', icon: FileText, keywords: 'акт выполненных работ чеки подписи sms смс клиенту' },
      { path: '/document-editor', label: 'Правка документов', icon: FilePenLine, keywords: 'исправить акт счёт упд редактировать' },
      { path: '/documents', label: 'Бланки и шаблоны', icon: Stamp, keywords: 'печать печати шаблон бланк реквизиты' },
      { path: '/onec-export', label: 'Выгрузка в 1С', icon: FileSpreadsheet, keywords: '1с 1c бухгалтерия excel эксель выгрузить' },
      { path: '/media', label: 'Медиафайлы', icon: Images, keywords: 'фото видео снимки место на диске' },
    ],
  },
  {
    id: 'team',
    title: 'Команда',
    items: [
      { path: '/employees', label: 'Сотрудники', icon: Users, keywords: 'мастера сантехники электрики диспетчеры пароль пригласить новый мастер' },
      { path: '/crews', label: 'Составы', icon: UsersRound, keywords: 'бригады бригада состав' },
      { path: '/technician-reports', label: 'Отчёты', icon: BarChart3, keywords: 'зарплата выплаты доля деньги заработок выручка' },
    ],
  },
  {
    id: 'reference',
    title: 'Справка',
    items: [
      { path: '/kb', label: 'Справочник', icon: BookOpen, keywords: 'цены прайс инструкции техкарты правила услуги' },
      { path: '/counterparties', label: 'Контрагенты', icon: ShieldCheck, keywords: 'фнс инн юрлица организации ликвидация проверка' },
    ],
  },
  {
    id: 'settings',
    bottom: true,
    items: [{ path: '/settings', label: 'Настройки', icon: SettingsIcon, keywords: 'версия приложения обновление звук уведомления' }],
  },
];

const COLLAPSED_GROUPS_KEY = 'truba-delo-nav-collapsed';
const COMPACT_KEY = 'truba-delo-nav-compact';
const WIDTH_KEY = 'truba-delo-nav-width';

// Ширину меню тянут мышкой за правую границу, как в Telegram (владелец, 15.10.2026: «можно
// двигать меню по границе, как в тг»). Сжали меньше COLLAPSE_AT — меню схлопывается до значков,
// потянули обратно — раскрывается. Двойной щелчок по границе возвращает обычную ширину
const DEFAULT_WIDTH = 260;
/** Ширина меню из одних значков — как .sidebar-compact в index.css */
const COMPACT_WIDTH = 60;
const MIN_WIDTH = 200;
const MAX_WIDTH = 420;
const COLLAPSE_AT = 140;
const KEYBOARD_STEP = 20;

function clampWidth(width: number): number {
  return Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round(width)));
}

function loadWidth(): number {
  const saved = Number(readStorage(WIDTH_KEY));
  return Number.isFinite(saved) && saved > 0 ? clampWidth(saved) : DEFAULT_WIDTH;
}

// Карта Leaflet подгоняет себя под размер только по resize окна, а здесь меняется лишь
// ширина области страницы — без этого сбоку на карте оставалась бы серая полоса
function refitPage() {
  requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
}

// Хранилище браузера бывает недоступно (приватный режим, запрет в настройках) —
// тогда меню просто не помнит свой вид между заходами, но работает
function readStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // не запомнилось — до перезагрузки меню всё равно выглядит так, как выбрали
  }
}

function loadCollapsedGroups(): Set<string> {
  try {
    const parsed: unknown = JSON.parse(readStorage(COLLAPSED_GROUPS_KEY) ?? '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : []);
  } catch {
    return new Set();
  }
}

// «ё» и «е» пишут вперемешку: «Отчёты» должны находиться и по «отчеты»
function normalizeForSearch(text: string): string {
  return text.trim().toLowerCase().replace(/ё/g, 'е');
}

// Кроме названия пункта смотрим и на название группы: «документы» покажет всю группу —
// в том числе «Бланки и шаблоны», которые раньше в меню так и назывались «Документы».
// И на другие слова пункта (keywords): «зарплата» найдёт «Отчёты». Каждое слово запроса —
// с начала какого-нибудь слова пункта: «акт» — «Акты», но «ты» — уже нет
function itemMatches(item: NavItem, words: string[]): boolean {
  const hay = normalizeForSearch(`${item.label} ${item.keywords ?? ''}`).split(/[\s«»()]+/);
  return words.every((w) => hay.some((h) => h.startsWith(w)));
}

function filterSections(query: string): NavSection[] {
  const words = query.split(/\s+/).filter(Boolean);
  return NAV_SECTIONS.map((section) =>
    section.title && normalizeForSearch(section.title).startsWith(query)
      ? section
      : { ...section, items: section.items.filter((item) => itemMatches(item, words)) },
  ).filter((section) => section.items.length > 0);
}

function isItemActive(item: NavItem, pathname: string): boolean {
  return matchPath({ path: item.path, end: item.end ?? false }, pathname) !== null;
}

export default function Layout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const unreadCount = useChatUnread((s) => s.unreadCount);
  const refreshUnread = useChatUnread((s) => s.refresh);
  const socket = useSocket('chat');
  const location = useLocation();
  // Общий список сантехников (выпадающие списки назначения, фильтры) обновляется по
  // сигналу с сервера — иначе нового сотрудника не было видно до перезагрузки страницы
  const reloadTechnicians = useTechniciansStore((s) => s.reload);
  useBoardUpdates(['employees'], reloadTechnicians);

  useEffect(() => {
    requestNotificationPermission();
    refreshUnread();
  }, [refreshUnread]);

  useEffect(() => {
    if (!socket) return;
    const onMessage = (message: ChatMessage) => {
      if (message.senderId === user?.id) return;
      // На странице чата непрочитанное сразу отмечается прочитанным сервером —
      // не дублируем счётчик, просто пересчитываем после короткой паузы
      refreshUnread();
      if (location.pathname !== '/chat') {
        playNotificationSound();
      }
      // Метка чата: прочитают тред — уведомление Windows закроется само (NotificationsBell).
      // Пишет диспетчерам сантехник, поэтому тред — по отправителю
      showBrowserNotification(`${message.sender?.fullName ?? 'Новое сообщение'} — ${BASE_TITLE}`, message.text, `direct:${message.senderId}`);
    };
    socket.on('chat:message', onMessage);

    const onChannelMessage = (message: GeneralChatMessage) => {
      if (message.senderId === user?.id) return;
      refreshUnread();
      if (location.pathname !== '/chat') {
        playNotificationSound();
      }
      showBrowserNotification(`${message.sender.fullName} — ${CHANNEL_LABELS[message.channel]}`, message.text, `channel:${message.channel}`);
    };
    socket.on('chat:channel:message', onChannelMessage);

    return () => {
      socket.off('chat:message', onMessage);
      socket.off('chat:channel:message', onChannelMessage);
    };
  }, [socket, user?.id, location.pathname, refreshUnread]);

  useEffect(() => {
    document.title = unreadCount > 0 ? `(${unreadCount}) ${BASE_TITLE}` : BASE_TITLE;
  }, [unreadCount]);

  useEffect(() => {
    if (location.pathname === '/chat') {
      refreshUnread();
    }
  }, [location.pathname, refreshUnread]);

  return (
    <div className="app-shell">
      <Sidebar chatUnread={unreadCount} userName={user?.fullName} onLogout={logout} />
      <main className="app-content">
        <div className="topbar">
          <div className="topbar-actions">
            <NotificationsBell />
          </div>
        </div>
        <UpdateBanner />
        <div className="app-content-body">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

interface SidebarProps {
  chatUnread: number;
  userName?: string;
  onLogout: () => void;
}

function Sidebar({ chatUnread, userName, onLogout }: SidebarProps) {
  const location = useLocation();
  const [compact, setCompact] = useState(() => readStorage(COMPACT_KEY) === '1');
  const [width, setWidth] = useState(loadWidth);
  const [collapsedGroups, setCollapsedGroups] = useState(loadCollapsedGroups);
  const [query, setQuery] = useState('');
  // На телефоне меню открыто только на той странице, где его открыли: любой переход
  // (пункт меню, Enter в поиске, «Назад» в браузере) закрывает его сам
  const [menuOpenedOn, setMenuOpenedOn] = useState<string | null>(null);
  const mobileMenuOpen = menuOpenedOn === location.pathname;
  const navRef = useRef<HTMLElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  // Ctrl+K (и «/» вне полей ввода) — к общему поиску из любого места панели
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const typing = target?.closest('input, textarea, select, [contenteditable="true"]');
      if (((e.ctrlKey || e.metaKey) && e.code === 'KeyK') || (e.key === '/' && !typing)) {
        e.preventDefault();
        if (compact) applyCompact(false);
        if (window.innerWidth <= 860) setMenuOpenedOn(location.pathname);
        // Поле появляется в меню только после перерисовки (узкое меню, меню телефона)
        requestAnimationFrame(() => searchRef.current?.focus());
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  // На невысоком экране меню прокручивается — пункт открытой страницы держим на виду,
  // даже если он в самом низу списка
  useEffect(() => {
    navRef.current?.querySelector('a.active')?.scrollIntoView({ block: 'nearest' });
  }, [location.pathname, mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpenedOn(null);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileMenuOpen]);

  const search = normalizeForSearch(query);
  // Набрали в английской раскладке («pfhgkfnf») — разделы ищем и по-русски, как и сервер
  const found = search ? filterSections(search) : NAV_SECTIONS;
  const switched = search && !found.length ? switchLayout(search) : null;
  const matched = switched ? filterSections(switched) : found;
  // Разделы не подошли — меню остаётся целым: найденное в нарядах, чате и т. д. показывает
  // выдача общего поиска, и «Ничего не найдено» в меню рядом с ней только путало
  const sections = matched.length ? matched : NAV_SECTIONS;
  const firstMatch = search && matched.length ? sections[0]?.items[0] : undefined;
  // Группу открытой сейчас страницы свернуть нельзя — иначе не видно, где находишься
  const activeGroupId = NAV_SECTIONS.find(
    (section) => section.title && section.items.some((item) => isItemActive(item, location.pathname)),
  )?.id;

  function toggleGroup(id: string) {
    const next = new Set(collapsedGroups);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setCollapsedGroups(next);
    writeStorage(COLLAPSED_GROUPS_KEY, JSON.stringify([...next]));
  }

  function applyCompact(next: boolean) {
    setCompact(next);
    // В узком меню поля поиска нет — недопечатанный запрос оставил бы от меню пару значков
    if (next) setQuery('');
    writeStorage(COMPACT_KEY, next ? '1' : '0');
  }

  function toggleCompact() {
    applyCompact(!compact);
    refitPage();
  }

  function applyWidth(next: number) {
    setWidth(next);
    writeStorage(WIDTH_KEY, String(next));
  }

  // Тянут за границу: ширина идёт за мышкой, у края — схлопывание до значков
  function startResize(e: ReactPointerEvent<HTMLDivElement>) {
    if (e.button !== 0) return;
    e.preventDefault();
    const handle = e.currentTarget;
    handle.setPointerCapture(e.pointerId);
    const startX = e.clientX;
    const startWidth = compact ? COMPACT_WIDTH : width;
    let lastWidth = width;
    let lastCompact = compact;
    document.body.classList.add('sidebar-resizing');

    const onMove = (ev: PointerEvent) => {
      const raw = startWidth + ev.clientX - startX;
      const nextCompact = raw < COLLAPSE_AT;
      if (nextCompact !== lastCompact) {
        lastCompact = nextCompact;
        setCompact(nextCompact);
        if (nextCompact) setQuery('');
      }
      if (!nextCompact) {
        lastWidth = clampWidth(raw);
        setWidth(lastWidth);
      }
      refitPage();
    };
    const onUp = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onUp);
      handle.removeEventListener('pointercancel', onUp);
      document.body.classList.remove('sidebar-resizing');
      // В хранилище — один раз, по отпусканию, а не на каждое движение мыши
      writeStorage(COMPACT_KEY, lastCompact ? '1' : '0');
      writeStorage(WIDTH_KEY, String(lastWidth));
    };
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }

  function resetWidth() {
    applyCompact(false);
    applyWidth(DEFAULT_WIDTH);
    refitPage();
  }

  // С клавиатуры: фокус на границе, стрелки влево/вправо
  function handleResizerKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    if (e.key === 'ArrowRight') {
      if (compact) applyCompact(false);
      else applyWidth(clampWidth(width + KEYBOARD_STEP));
    } else if (!compact) {
      if (width <= MIN_WIDTH) applyCompact(true);
      else applyWidth(clampWidth(width - KEYBOARD_STEP));
    }
    refitPage();
  }

  function afterNavigate() {
    setQuery('');
    setMenuOpenedOn(null);
  }

  const sidebarClass = ['sidebar', compact && 'sidebar-compact', mobileMenuOpen && 'sidebar-mobile-open']
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <aside className={sidebarClass} style={{ '--sidebar-width': `${width}px` } as CSSProperties}>
        <div
          className="sidebar-resizer"
          role="separator"
          aria-orientation="vertical"
          aria-label="Ширина меню"
          aria-valuemin={MIN_WIDTH}
          aria-valuemax={MAX_WIDTH}
          aria-valuenow={compact ? COMPACT_WIDTH : width}
          tabIndex={0}
          title="Потяните, чтобы изменить ширину меню. Двойной щелчок — обычная ширина"
          onPointerDown={startResize}
          onDoubleClick={resetWidth}
          onKeyDown={handleResizerKeyDown}
        />
        <div className="sidebar-title">
          <span className="sidebar-brand">
            <Wrench size={19} strokeWidth={2.3} />
            <span className="sidebar-brand-text">
              <span className="sidebar-brand-name">Control Brigade</span>
              <span className="sidebar-brand-company">Труба-Дело</span>
            </span>
          </span>
          <button
            type="button"
            className="sidebar-icon-btn sidebar-compact-toggle"
            onClick={toggleCompact}
            title={compact ? 'Развернуть меню' : 'Свернуть меню до значков'}
            aria-label={compact ? 'Развернуть меню' : 'Свернуть меню'}
          >
            {compact ? <PanelLeftOpen size={ICON_SIZE} /> : <PanelLeftClose size={ICON_SIZE} />}
          </button>
          <button
            type="button"
            className="sidebar-menu-toggle"
            onClick={() => setMenuOpenedOn(mobileMenuOpen ? null : location.pathname)}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X size={ICON_SIZE} /> : <Menu size={ICON_SIZE} />}
            Меню
          </button>
        </div>
        <div className="sidebar-body">
          <GlobalSearch
            query={query}
            onQueryChange={setQuery}
            sections={search && matched.length ? sections.flatMap((section) => section.items) : []}
            onNavigate={afterNavigate}
            inputRef={searchRef}
          />
          <nav ref={navRef}>
            {sections.map((section) => {
              // Во время поиска найденное показываем целиком, без сворачивания
              const canCollapse = Boolean(section.title) && !search && section.id !== activeGroupId;
              const collapsed = canCollapse && collapsedGroups.has(section.id);
              const sectionClass = [
                'nav-section',
                collapsed && 'nav-group-collapsed',
                section.bottom && 'nav-section-bottom',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <div key={section.id} className={sectionClass}>
                  {section.title &&
                    (canCollapse ? (
                      <button
                        type="button"
                        className="nav-group-header"
                        onClick={() => toggleGroup(section.id)}
                        aria-expanded={!collapsed}
                      >
                        {section.title}
                        <ChevronDown size={14} className="nav-group-chevron" />
                      </button>
                    ) : (
                      <div className="nav-group-header">{section.title}</div>
                    ))}
                  <div className="nav-group-items">
                    {section.items.map((item) => {
                      const count = item.badge === 'chatUnread' ? chatUnread : 0;
                      return (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          end={item.end}
                          title={compact ? item.label : undefined}
                          className={item === firstMatch ? 'nav-first-match' : undefined}
                          onClick={afterNavigate}
                        >
                          <item.icon size={ICON_SIZE} />
                          <span className="nav-label">{item.label}</span>
                          {count > 0 && <span className="badge nav-badge">{count}</span>}
                        </NavLink>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
          <div className="sidebar-footer">
            <div className="muted sidebar-user">{userName}</div>
            <button className="btn-link" onClick={onLogout} title={compact ? 'Выйти' : undefined}>
              <LogOut size={15} />
              <span className="sidebar-logout-label">Выйти</span>
            </button>
          </div>
        </div>
      </aside>
      {mobileMenuOpen && <div className="sidebar-backdrop" onClick={() => setMenuOpenedOn(null)} />}
    </>
  );
}
